import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, IconButton, Dialog,Grid, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert, CircularProgress, MenuItem } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, Refresh, ChevronLeft, ChevronRight, Public } from '@mui/icons-material';
import { getCountries, createCountry, updateCountry, deleteCountry } from '../../services/api';

const defaultForm = { name: '', code: '' };

const Country = () => {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [filter, setFilter] = useState({ name: '', code: '' });
  const [searchTimeout, setSearchTimeout] = useState(null);
  const totalPages = Math.ceil(total / rowsPerPage) || 1;

  const fetchData = useCallback(async (pageArg, rowsArg, filterArg = filter) => {
    setLoading(true);
    const params = {
      page: pageArg + 1,
      page_size: rowsArg,
      name: filterArg.name || undefined,
      code: filterArg.code || undefined,
    };
    try {
      const res = await getCountries(params);
      setCountries(res.results);
      setTotal(res.total);
    } catch (e) {
      setCountries([]);
      setTotal(0);
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    fetchData(page, rowsPerPage);
  }, [page, rowsPerPage, filter, fetchData]);

  // Pagination handlers
  const handlePrevPage = () => { if (page > 0) setPage(page - 1); };
  const handleNextPage = () => { if (page < totalPages - 1) setPage(page + 1); };
  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };
  const handleCustomRowsInputChange = (e) => { setCustomRowsInput(e.target.value.replace(/\D/g, '')); };
  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) { setRowsPerPage(val); setPage(0); }
    setCustomRowsActive(false);
  };
  const handleRefresh = () => { setPage(0); fetchData(0, rowsPerPage); };

  // CRUD handlers
  const handleOpen = (country = null) => {
    setEditId(country ? country.id : null);
    setForm(country ? { name: country.name } : defaultForm);
    setOpen(true);
  };
  const handleClose = () => { setOpen(false); setForm(defaultForm); setEditId(null); };
  const handleSave = async () => {
    if (!form.name.trim()) {
      setSnackbar({ open: true, message: 'Country name is required', severity: 'error' });
      return;
    }
    if (editId) {
      await updateCountry(editId, form);
      setSnackbar({ open: true, message: 'Country updated', severity: 'success' });
    } else {
      await createCountry(form);
      setSnackbar({ open: true, message: 'Country added', severity: 'success' });
    }
    handleClose();
    fetchData(page, rowsPerPage);
  };
  const handleDelete = async (id) => {
    await deleteCountry(id);
    setSnackbar({ open: true, message: 'Country deleted', severity: 'success' });
    setDeleteModal({ open: false, id: null });
    fetchData(page, rowsPerPage);
  };

  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: (page * rowsPerPage) + idx + 1
    }));

  const columns = [
    {
      field: 'sn',
      headerName: 'S/N',
      width: 80,
      sortable: false,
      filterable: false,
    },
    { field: 'name', headerName: 'Country Name', flex: 1 },
    { field: 'code', headerName: 'Country Code', width: 120 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <Box>
          <IconButton onClick={() => handleOpen(params.row)}><Edit /></IconButton>
          <IconButton color="error" onClick={() => setDeleteModal({ open: true, id: params.row.id })}><Delete /></IconButton>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Public sx={{ color: '#DC143C' }} />
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
            Country Management
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', ml: 'auto' }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' }, minWidth: 'auto' }}
          >
            Add Country
          </Button>
          <IconButton onClick={handleRefresh}><Refresh /></IconButton>
        </Box>
      </Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            label="Search Name"
            value={filter.name}
            onChange={e => {
              const val = e.target.value;
              setFilter(f => ({ ...f, name: val }));
              if (searchTimeout) clearTimeout(searchTimeout);
              setSearchTimeout(setTimeout(() => fetchData(0, rowsPerPage, { ...filter, name: val }), 500));
              setPage(0);
            }}
            size="small"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            label="Search Code"
            value={filter.code}
            onChange={e => {
              const val = e.target.value;
              setFilter(f => ({ ...f, code: val }));
              if (searchTimeout) clearTimeout(searchTimeout);
              setSearchTimeout(setTimeout(() => fetchData(0, rowsPerPage, { ...filter, code: val }), 500));
              setPage(0);
            }}
            size="small"
            fullWidth
          />
        </Grid>
      </Grid>
      <Box sx={{
        width: '100%',
        overflowX: 'auto',
        minHeight: 520,
        '&::-webkit-scrollbar': {
          height: 8,
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(0,0,0,0.1)',
          borderRadius: 4,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.3)',
          borderRadius: 4,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgba(0,0,0,0.5)',
        },
      }}>
        <DataGrid
          rows={addSerialNumbers(countries, page, rowsPerPage)}
          columns={columns}
          page={page}
          onPageChange={(newPage) => setPage(newPage)}
          pageSize={rowsPerPage}
          onPageSizeChange={(newPageSize) => { setRowsPerPage(newPageSize); setPage(0); }}
          pagination
          rowCount={total}
          paginationMode="server"
          rowsPerPageOptions={[10, 20, 50, 100]}
          disableSelectionOnClick
          loading={loading}
          hideFooter
          sx={{
            border: 'none',
            minWidth: 1200, // Ensure minimum width for all columns
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#DC143C',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: 'white',
              fontWeight: 'bold',
              fontSize: { xs: '0.875rem', sm: '1rem' }
            },
            '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
              color: 'white'
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#FFF0F3'
            },
            '& .MuiDataGrid-cell': {
              fontSize: { xs: '0.875rem', sm: '1rem' },
              padding: { xs: '8px 4px', sm: '8px 12px' }
            }
          }}
        />
      </Box>
      {/* Custom Pagination Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: { xs: 'center', sm: 'flex-end' }, 
        mt: 3, 
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevPage} disabled={page === 0}><ChevronLeft /></IconButton>
          <Typography variant="body2">Page {totalPages === 0 ? 0 : page + 1} of {totalPages}</Typography>
          <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}><ChevronRight /></IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">Rows per page:</Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ width: 100 }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ width: 100 }}
              inputProps={{ min: 1, style: { textAlign: 'center' } }}
              placeholder="Rows"
            />
          )}
        </Box>
      </Box>
      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>{editId ? 'Edit Country' : 'Add Country'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Country Name"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            fullWidth
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteModal.open} onClose={() => setDeleteModal({ open: false, id: null })}>
        <DialogTitle>Delete Country</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this country?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModal({ open: false, id: null })}>Cancel</Button>
          <Button onClick={() => handleDelete(deleteModal.id)} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Country; 